import { css } from 'aphrodite';
import * as React from 'react';
import { ICustomizationMarker } from '../../../models/utils/templateCustomizationUtils';
import { Button } from '../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../components-v2/Dialog';
import { WarningIcon, WarningIconType } from '../svgs/icons/WarningIcon';
import { styleSheet } from './styles';

const MaxMarkersToList = 10;

export interface ICustomizationRequiredDialogProps {
	/** Drives the wording, since the same safeguard guards email campaigns and social posts. */
	contentKind?: 'campaign' | 'post';
	isOpen: boolean;
	markers: ICustomizationMarker[];
	onRequestClose(): void;
}

/**
 * Advisory only: a single "Close window" action rather than a "Continue anyway", because the caller lets
 * the next click through. The user proceeds by pressing the same button again, which keeps a false
 * positive from becoming a hard block while still costing a deliberate second action.
 */
export const CustomizationRequiredDialog: React.FC<ICustomizationRequiredDialogProps> = ({
	contentKind = 'campaign',
	isOpen,
	markers,
	onRequestClose,
}) => {
	const onOpenChange = (open: boolean) => {
		if (!open) {
			onRequestClose();
		}
	};

	const markersToList = markers.slice(0, MaxMarkersToList);
	const hiddenMarkerCount = markers.length - markersToList.length;
	const isPost = contentKind === 'post';

	return (
		<Dialog dismissOnOutsideClick={false} onOpenChange={onOpenChange} open={isOpen}>
			<DialogPopup styles={[styleSheet.popup]}>
				<DialogHeader styles={[styleSheet.header]}>
					<WarningIcon className={css(styleSheet.icon)} type={WarningIconType.Red} />
					<DialogTitle styles={[styleSheet.title]}>
						{`This ${isPost ? 'post' : 'campaign'} has text that requires customization.`}
					</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<p className={css(styleSheet.description)}>
						{`Please review the following before ${isPost ? 'posting' : 'sending'}, then close this window and continue.`}
					</p>
					<ul className={css(styleSheet.markerList)}>
						{markersToList.map((marker, i) => (
							<li className={css(styleSheet.marker)} key={`${marker.location}-${i}-${marker.text}`}>
								<span className={css(styleSheet.markerLocation)}>
									{marker.location === 'subject' ? 'Subject line' : isPost ? 'Post content' : 'Email body'}
								</span>
								<span className={css(styleSheet.markerText)}>{marker.text}</span>
							</li>
						))}
					</ul>
					{hiddenMarkerCount > 0 ? (
						<p className={css(styleSheet.hiddenCount)}>
							{`and ${hiddenMarkerCount} more ${hiddenMarkerCount === 1 ? 'item' : 'items'}`}
						</p>
					) : null}
				</DialogBody>
				<DialogFooter>
					<DialogClose render={<Button variant='primary'>Close window</Button>} />
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
